import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GetBookingPageSettingsAction,
  GetOrganizationAction,
  GetSubscriptionAction,
  SaveBookingPageSettingsAction,
} from "../../../store/action";
import {
  alertErrorClass,
  btnPrimaryClass,
  btnSecondaryClass,
  pageSubtitleClass,
  pageTitleClass,
  tabButtonClass,
  tabListClass,
} from "../dashboardStyles";
import EmbedWidgetCard from "./EmbedWidgetCard";
import HelpLinks from "./wizard/components/HelpLinks";
import ConfirmDialog from "../ConfirmDialog";
import SitePreview from "../../site-preview/SitePreview";
import SiteSectionTabs from "./site/SiteSectionTabs";
import { AboutPanel, BrandPanel, ContactPanel, HeaderPanel, SocialPanel } from "./site/SitePanels";
import { PlanLimits } from "../../../constants/subscription";
import { SITE_VIDEOS } from "../../../constants/support";
import { brandingSaveError } from "../../../utils/brandingImage";
import { notify, notifyFailure } from "../../../utils/notify";
import { useUnsavedChangesGuard } from "../../../hooks/useUnsavedChangesGuard";
import { useObjectUrl } from "../../../hooks/useObjectUrl";

/**
 * Site & widget — the two ways customers reach the booking form: our public
 * booking page (`/:organizationSlug`), and the widget a business puts on its
 * own site. Two tabs, because a business only cares about one at a time.
 *
 * The Site tab edits the page one section at a time — header, About, contact,
 * social links, visual identity — beside a live preview of the real page drawn
 * from the draft (components/site-preview). Every section is one view of the
 * same form state, held here: switching sections or tabs never loses an edit,
 * and one Save writes all of them. Every field is optional: whatever is left
 * blank is omitted from the public page rather than rendered empty.
 *
 * The Widget tab saves nothing — the embed snippet is derived from the slug,
 * the mode and the button text, and it reads its colour from the draft.
 */

const EMPTY_FORM = {
  bookingTitle: "",
  heroDescription: "",
  ctaButtonText: "",
  aboutText: "",
  streetAddress: "",
  bookingCity: "",
  contactPhone: "",
  contactEmail: "",
  openingHours: "",
  facebookUrl: "",
  instagramUrl: "",
  primaryColor: "#5c9f24",
  secondaryColor: "#1a1d19",
  logoUrl: "",
  headerImageUrl: "",
  removeLogo: false,
  removeHeaderImage: false,
};

const MySite = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("site");
  const [section, setSection] = useState("header");
  const [formData, setFormData] = useState(EMPTY_FORM);
  // The form as last loaded or saved, to tell an edit from no edit.
  const [savedForm, setSavedForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [headerImageFile, setHeaderImageFile] = useState(null);
  const [businessName, setBusinessName] = useState("");
  // The stored address, never one rebuilt from the name (utils/publicPagePath.js).
  const [slug, setSlug] = useState("");
  const [showsBadge, setShowsBadge] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [headerBusy, setHeaderBusy] = useState(false);
  const [aboutBusy, setAboutBusy] = useState(false);
  // The About block being edited — the editor's list and the preview's outline
  // both follow it — and whether About's HTML mode holds an unapplied draft.
  const [aboutBlock, setAboutBlock] = useState(0);
  const [aboutStatus, setAboutStatus] = useState({ mode: "visual", pending: false });
  // The load failure only. A save reports through the snackbar.
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // A brand-new organization has no settings row yet — that 404 is the
      // expected empty state, not an error. The plan only decides whether the
      // preview carries the free tier's badge; not knowing it is no reason to
      // refuse to open the editor.
      const [settings, organization, subscription] = await Promise.all([
        GetBookingPageSettingsAction().catch((err) => {
          if (err.response?.status === 404) return null;
          throw err;
        }),
        GetOrganizationAction().catch(() => null),
        GetSubscriptionAction().catch(() => null),
      ]);

      if (settings) {
        // Null columns become "" so every input stays controlled.
        const loaded = {
          ...EMPTY_FORM,
          ...Object.fromEntries(
            Object.keys(EMPTY_FORM).map((key) => [key, settings[key] ?? EMPTY_FORM[key]]),
          ),
        };
        setFormData(loaded);
        setSavedForm(loaded);
      }
      setBusinessName(organization?.businessName || "");
      setSlug(organization?.slug || "");
      const plan = subscription?.effectivePlan ?? subscription?.plan;
      setShowsBadge(Boolean(PlanLimits[plan]?.showsPlatformBadge));
      setError(null);
    } catch (err) {
      console.error("Error loading site settings:", err);
      setError(t("Dashboard.MySite.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () =>
      Boolean(logoFile || headerImageFile) ||
      Object.keys(EMPTY_FORM).some((key) => formData[key] !== savedForm[key]),
    [formData, savedForm, logoFile, headerImageFile],
  );
  const leaveGuard = useUnsavedChangesGuard(dirty && !loading);

  const logoPreview = useObjectUrl(logoFile);
  const headerPreview = useObjectUrl(headerImageFile);
  const shownLogo = logoPreview || (formData.removeLogo ? "" : formData.logoUrl);

  // The draft as the public page would read it: unsaved images as blob: URLs,
  // removed ones gone. Memoised, so the preview is redrawn on edits only.
  const previewSettings = useMemo(
    () => ({
      ...formData,
      businessName,
      logoUrl: shownLogo,
      headerImageUrl:
        headerPreview || (formData.removeHeaderImage ? "" : formData.headerImageUrl),
    }),
    [formData, businessName, shownLogo, headerPreview],
  );

  // A click in the preview: a section, or within About one of its blocks.
  const selectFromPreview = useCallback((next, block = null) => {
    setSection(next);
    if (next === "about" && block !== null) setAboutBlock(block);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const busy = saving || logoBusy || headerBusy || aboutBusy;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;

    // The browser's own check cannot point at a field in a hidden section (it
    // gives up: "not focusable"), so the form is noValidate and this does it:
    // open the section holding the first bad field, then let the browser say
    // what is wrong with it there.
    const invalid = [...e.currentTarget.elements].find(
      (element) => element.willValidate && !element.checkValidity(),
    );
    if (invalid) {
      const panel = invalid.closest("[data-site-section]");
      if (panel) setSection(panel.dataset.siteSection);
      requestAnimationFrame(() => invalid.reportValidity());
      return;
    }

    setSaving(true);
    try {
      const settings = await SaveBookingPageSettingsAction(formData, logoFile, headerImageFile);
      const saved = {
        ...formData,
        logoUrl: settings.logoUrl || "",
        headerImageUrl: settings.headerImageUrl || "",
        removeLogo: false,
        removeHeaderImage: false,
      };
      setFormData(saved);
      setSavedForm(saved);
      setLogoFile(null);
      setHeaderImageFile(null);
      notify.success(t("Dashboard.MySite.saved"));
    } catch (err) {
      console.error("Error saving site settings:", err);
      notifyFailure(err, brandingSaveError(err, t, "Dashboard.MySite.saveError"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className={pageSubtitleClass}>{t("Dashboard.Menu.loading")}</p>;
  }

  const panels = {
    header: (
      <HeaderPanel
        formData={formData}
        onChange={handleChange}
        logoPreview={shownLogo}
        businessName={businessName}
        onOpenBrand={() => setSection("brand")}
      />
    ),
    about: (
      <AboutPanel
        value={formData.aboutText}
        disabled={saving}
        onBusyChange={setAboutBusy}
        onValueChange={(value) => setFormData((current) => ({ ...current, aboutText: value }))}
        selectedBlock={aboutBlock}
        onSelectBlock={setAboutBlock}
        onStatusChange={setAboutStatus}
      />
    ),
    contact: <ContactPanel formData={formData} onChange={handleChange} />,
    social: <SocialPanel formData={formData} onChange={handleChange} />,
    brand: (
      <BrandPanel
        formData={formData}
        onChange={handleChange}
        logoFile={logoFile}
        headerImageFile={headerImageFile}
        disabled={saving}
        onLogoBusyChange={setLogoBusy}
        onHeaderBusyChange={setHeaderBusy}
        onLogoChange={(file, removed) => {
          setLogoFile(file);
          setFormData((current) => ({ ...current, removeLogo: removed }));
        }}
        onHeaderChange={(file, removed) => {
          setHeaderImageFile(file);
          setFormData((current) => ({ ...current, removeHeaderImage: removed }));
        }}
      />
    ),
  };

  return (
    <div>
      {/* Help where it is hardest to manage alone: a demo of what is on
          screen, and a person. */}
      <div className="-mt-2 mb-4 flex items-center justify-end gap-0.5 border-b border-ink-200 pb-2 sm:gap-1 lg:-mt-4">
        <HelpLinks
          videoUrl={
            activeTab === "widget"
              ? SITE_VIDEOS.widget
              : section === "about"
                ? SITE_VIDEOS.about
                : SITE_VIDEOS.site
          }
        />
      </div>

      <div className="mb-5 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h1 className={pageTitleClass}>{t("Dashboard.MySite.title")}</h1>
          <p className={`${pageSubtitleClass} mb-0`}>
            {t(activeTab === "widget" ? "Dashboard.MySite.widgetSubtitle" : "Dashboard.MySite.siteSubtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <span aria-live="polite" className="contents">
            {(dirty || aboutStatus.pending) && (
              <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-700">
                <span className="size-2 rounded-full bg-amber-500" aria-hidden="true" />
                {/* Unapplied HTML is why Save is off; say that rather than "unsaved". */}
                {t(aboutStatus.pending ? "Dashboard.MySite.applyHtmlFirst" : "Dashboard.MySite.unsaved")}
              </span>
            )}
          </span>
          {slug && (
            <a
              href={`/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`${btnSecondaryClass} no-underline`}
            >
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
              {t("Dashboard.MySite.viewSite")}
            </a>
          )}
          {activeTab === "site" && (
            <button type="submit" form="site-form" className={btnPrimaryClass} disabled={busy}>
              <i className="bi bi-floppy" aria-hidden="true" />
              {saving ? t("Dashboard.MySite.saving") : t("Dashboard.MySite.save")}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className={alertErrorClass} role="alert">
          {error}
        </div>
      )}

      <div className={tabListClass} role="tablist" aria-label={t("Dashboard.MySite.tabs.label")}>
        {["site", "widget"].map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            id={`mysite-tab-${tab}`}
            aria-selected={activeTab === tab}
            aria-controls={`mysite-panel-${tab}`}
            disabled={saving || aboutBusy}
            tabIndex={activeTab === tab ? 0 : -1}
            className={tabButtonClass(activeTab === tab)}
            onClick={() => setActiveTab(tab)}
          >
            {t(`Dashboard.MySite.tabs.${tab}`)}
          </button>
        ))}
      </div>

      {activeTab === "widget" ? (
        <div id="mysite-panel-widget" role="tabpanel" aria-labelledby="mysite-tab-widget">
          <EmbedWidgetCard
            slug={slug}
            primaryColor={formData.primaryColor}
            savedColor={savedForm.primaryColor}
            defaultLabel={formData.ctaButtonText}
            onChangeColor={() => {
              setActiveTab("site");
              setSection("brand");
            }}
          />
        </div>
      ) : (
        <div id="mysite-panel-site" role="tabpanel" aria-labelledby="mysite-tab-site">
          <SiteSectionTabs active={section} onChange={setSection} disabled={saving} />

          <div className="mt-4 grid items-start gap-5 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
            <form id="site-form" onSubmit={handleSubmit} className="min-w-0" noValidate>
              {/* Every panel stays mounted: the About editor keeps its undo
                  history and an image being cropped keeps its dialog when the
                  section changes. Only the one being edited is shown. */}
              {Object.entries(panels).map(([id, panel]) => (
                <div
                  key={id}
                  id={`site-section-${id}`}
                  data-site-section={id}
                  role="tabpanel"
                  aria-labelledby={`site-section-tab-${id}`}
                  hidden={section !== id}
                >
                  {panel}
                </div>
              ))}
            </form>

            <SitePreview
              settings={previewSettings}
              showsBadge={showsBadge}
              section={section}
              block={section === "about" ? aboutBlock : null}
              onSelectSection={selectFromPreview}
              address={slug ? `${window.location.host}/${slug}` : null}
              status={
                section === "about" && aboutStatus.mode === "html" ? (
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold ${
                      aboutStatus.pending ? "text-amber-700" : "text-brand-700"
                    }`}
                  >
                    <i
                      className={`bi ${aboutStatus.pending ? "bi-clock-history" : "bi-check-circle-fill"}`}
                      aria-hidden="true"
                    />
                    {t(aboutStatus.pending ? "Dashboard.MySite.preview.showsApplied" : "Dashboard.MySite.preview.applied")}
                  </span>
                ) : null
              }
            />
          </div>
        </div>
      )}

      {leaveGuard.pending && (
        <ConfirmDialog
          message={t("Dashboard.MySite.leave.message")}
          confirmLabel={t("Dashboard.MySite.leave.confirm")}
          cancelLabel={t("Dashboard.MySite.leave.cancel")}
          onConfirm={leaveGuard.proceed}
          onCancel={leaveGuard.stay}
        />
      )}
    </div>
  );
};

export default MySite;
